import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import styled from 'styled-components';

import { Flame } from '@sorare/core/src/atoms/icons/Flame.tsx';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { BrandS, BrandXS } from '@sorare/core/src/atoms/typography';
import { Bezel } from '@sorare/core/src/atoms/ui/bezels/Bezel/index.tsx';
import { RewardConfigImage } from '@sorare/core/src/components/rewards/AnyRewardConfig/RewardConfigImage/index.tsx';
import { RewardConfigText } from '@sorare/core/src/components/rewards/AnyRewardConfig/RewardConfigText/index.tsx';
import { absCenter } from '@sorare/core/src/lib/style.ts';

import type { RewardsContent_cardSetRoot } from './__generated__/index.graphql.ts';

const RewardsWrapper = styled(Vertical)`
  padding: var(--unit) var(--double-unit);
`;
const IconWrapper = styled(Horizontal).attrs({ center: true })`
  position: relative;
`;
const VerticalLine = styled.div`
  width: var(--half-unit);
  background: var(--c-nd-50);
  position: absolute;
  top: var(--double-unit);
  left: 50%;
  transform: translateX(-50%);
  height: calc(100% + var(--quadruple-unit));
  z-index: -1;

  &.highlighted {
    background: var(--c-set-static-surface-primary);
    height: calc(100% + calc(11.5 * var(--unit)));
  }
`;
const CenteredNumber = styled(BrandS)`
  ${absCenter}
  isolation: isolate;

  &::before {
    content: '';
    position: absolute;
    inset: calc(-1 * var(--half-unit));
    border-radius: 50%;
    background-color: var(--c-black);
    filter: blur(8px);
    z-index: -1;
  }
`;
const StyledBezel = styled(Bezel)`
  opacity: 0.5;
  width: 100%;

  &.highlighted {
    opacity: 1;
  }
`;
const BezelContent = styled(SBHorizontal).attrs({ gap: 3 })`
  padding: var(--double-unit) calc(5.5 * var(--unit)) var(--double-unit)
    var(--unit);
`;

const ItemWrapper = styled.div`
  position: relative;
  flex: 1;
`;

const RewardImgWrapper = styled.div`
  position: absolute;
  right: 0;
  top: -10%;
  z-index: 1;
  filter: grayscale(50%);
  &.highlighted {
    filter: grayscale(0%);
  }
`;

type Props = {
  cardSetRoot?: RewardsContent_cardSetRoot;
};
export const RewardsContent = ({ cardSetRoot }: Props) => {
  const { inviteTasksAsReferrerRewardConfigs } = cardSetRoot || {};
  const reversedRewardConfigs = [
    ...(inviteTasksAsReferrerRewardConfigs || []),
  ].reverse();

  return (
    <RewardsWrapper>
      {reversedRewardConfigs?.map(({ id, rewardConfigs, title }, index) => {
        const displayedNumber = reversedRewardConfigs.length - index - 1;
        const isHighlighted = displayedNumber === 0;
        return (
          <Horizontal key={id} gap={4}>
            <IconWrapper>
              <VerticalLine
                className={classNames({ highlighted: isHighlighted })}
              />
              <Flame
                size={4}
                color={
                  isHighlighted
                    ? 'var(--c-set-static-surface-primary)'
                    : 'var(--c-nd-400)'
                }
              />
              <CenteredNumber>{displayedNumber}</CenteredNumber>
            </IconWrapper>
            {rewardConfigs.map(rewardConfig => (
              <ItemWrapper key={rewardConfig.id}>
                <StyledBezel
                  borderColor={'var(--c-set-static-surface-primary)'}
                  boxShadow={
                    isHighlighted
                      ? 'inset 0px -20px 20px -20px var(--c-set-static-surface-primary)'
                      : undefined
                  }
                  background="conic-gradient(from 90deg at 4px 4px, var(--c-nd-100) 25%, var(--c-black) 0) 0 0/5px 5px"
                  className={classNames({ highlighted: isHighlighted })}
                >
                  <BezelContent>
                    <BrandXS>{title}</BrandXS>
                    <BrandS>
                      <RewardConfigText anyRewardConfig={rewardConfig} />
                    </BrandS>
                  </BezelContent>
                </StyledBezel>
                <RewardImgWrapper
                  className={classNames({ highlighted: isHighlighted })}
                >
                  <RewardConfigImage anyRewardConfig={rewardConfig} size={5} />
                </RewardImgWrapper>
              </ItemWrapper>
            ))}
          </Horizontal>
        );
      })}
    </RewardsWrapper>
  );
};

RewardsContent.fragments = {
  cardSetRoot: gql`
    fragment RewardsContent_cardSetRoot on CardSetRoot {
      id
      inviteTasksAsReferrerRewardConfigs(sport: $sport) {
        id
        title
        rewardConfigs {
          id
          ...RewardConfigText_anyRewardConfigInterface
          ...RewardConfigImage_anyRewardConfigInterface
        }
      }
    }
    ${RewardConfigText.fragments.anyRewardConfigInterface}
    ${RewardConfigImage.fragments.anyRewardConfigInterface}
  ` as TypedDocumentNode<RewardsContent_cardSetRoot>,
};
